import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import * as ImagePicker from "expo-image-picker";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  View,
  type ViewStyle,
} from "react-native";
import Avatar from "./Avatar";
import ThemedText from "./ThemedText";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
type AvatarVariant = "circular" | "rounded" | "square";

interface AvatarUploadProps {
  name?: string;
  src?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  onImageSelect?: (uri: string) => void;
  onImageUpload?: (uri: string) => Promise<string>; // Upload to server and return URL
  disabled?: boolean;
  style?: ViewStyle;
  showChangeText?: boolean;
  allowRemove?: boolean;
  onRemove?: () => void;
}

export default function AvatarUpload({
  name,
  src,
  size = "xl",
  variant = "circular",
  onImageSelect,
  onImageUpload,
  disabled = false,
  style,
  showChangeText = true,
  allowRemove = true,
  onRemove,
}: AvatarUploadProps) {
  const primaryColor = useThemeColor({}, "primary");
  const textColor = useThemeColor({}, "text");

  const [imageUri, setImageUri] = useState<string | undefined>(src);
  const [uploading, setUploading] = useState(false);

  const requestPermission = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert(
        "Permission Required",
        "Please allow access to your photo library to upload an avatar.",
        [{ text: "OK" }]
      );
      return false;
    }
    return true;
  };

  const pickImage = async () => {
    if (disabled || uploading) return;

    const hasPermission = await requestPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const uri = result.assets[0].uri;
        setImageUri(uri);
        onImageSelect?.(uri);

        // If upload handler provided, upload to server
        if (onImageUpload) {
          setUploading(true);
          try {
            const uploadedUrl = await onImageUpload(uri);
            setImageUri(uploadedUrl);
          } catch (error) {
            Alert.alert("Upload Failed", "Failed to upload image. Please try again.");
            console.error("Upload error:", error);
          } finally {
            setUploading(false);
          }
        }
      }
    } catch (error) {
      console.error("Error picking image:", error);
      Alert.alert("Error", "Failed to pick image. Please try again.");
    }
  };

  const takePhoto = async () => {
    if (disabled || uploading) return;

    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      Alert.alert(
        "Permission Required",
        "Please allow camera access to take a photo.",
        [{ text: "OK" }]
      );
      return;
    }

    try {
      const result = await ImagePicker.launchCameraAsync({
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const uri = result.assets[0].uri;
        setImageUri(uri);
        onImageSelect?.(uri);

        // If upload handler provided, upload to server
        if (onImageUpload) {
          setUploading(true);
          try {
            const uploadedUrl = await onImageUpload(uri);
            setImageUri(uploadedUrl);
          } catch (error) {
            Alert.alert("Upload Failed", "Failed to upload image. Please try again.");
            console.error("Upload error:", error);
          } finally {
            setUploading(false);
          }
        }
      }
    } catch (error) {
      console.error("Error taking photo:", error);
      Alert.alert("Error", "Failed to take photo. Please try again.");
    }
  };

  const showImageOptions = () => {
    const baseOptions = [
      { text: "Choose from Library", onPress: () => void pickImage() },
      { text: "Take Photo", onPress: () => void takePhoto() },
    ];

    if (allowRemove && imageUri) {
      baseOptions.push({
        text: "Remove Photo",
        onPress: () => void handleRemove(),
      });
    }

    baseOptions.push({ text: "Cancel", onPress: () => {} });

    Alert.alert("Change Avatar", "Choose an option", baseOptions);
  };

  const handleRemove = () => {
    setImageUri(undefined);
    onRemove?.();
  };

  return (
    <View style={[styles.container, style]}>
      <Pressable
        onPress={showImageOptions}
        disabled={disabled || uploading}
        style={({ pressed }) => [
          styles.avatarContainer,
          pressed && !disabled && !uploading && styles.pressed,
        ]}
      >
        <Avatar
          name={name}
          src={imageUri}
          size={size}
          variant={variant}
        />

        {/* Upload Button Overlay */}
        <View
          style={[
            styles.uploadButton,
            {
              backgroundColor: primaryColor,
            },
          ]}
        >
          {uploading ? (
            <ActivityIndicator color="#FFFFFF" size="small" />
          ) : (
            <Ionicons name="camera" size={16} color="#FFFFFF" />
          )}
        </View>

        {disabled && <View style={styles.disabledOverlay} />}
      </Pressable>

      {showChangeText && !uploading && (
        <Pressable
          onPress={showImageOptions}
          disabled={disabled}
          style={({ pressed }) => [pressed && styles.textPressed]}
        >
          <ThemedText
            style={[
              styles.changeText,
              {
                color: disabled ? textColor + "60" : primaryColor,
              },
            ]}
          >
            {imageUri ? "Change Photo" : "Upload Photo"}
          </ThemedText>
        </Pressable>
      )}

      {uploading && (
        <ThemedText style={[styles.uploadingText, { color: textColor }]}>
          Uploading...
        </ThemedText>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
  },
  avatarContainer: {
    position: "relative",
  },
  pressed: {
    opacity: 0.7,
  },
  uploadButton: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
  },
  disabledOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderRadius: 100,
  },
  changeText: {
    marginTop: 8,
    fontSize: 14,
    fontWeight: "600",
  },
  textPressed: {
    opacity: 0.7,
  },
  uploadingText: {
    marginTop: 8,
    fontSize: 14,
  },
});
